import { defineStore } from 'pinia'

interface LogActivity {
    id: string
    old_values: Record<string, unknown> | null
    new_values: Record<string, unknown> | null
    created_at: string
    causer?: {
        id: string
        name: string
    }
}

interface CampaignDetailState {
    campaign: Campaign | null
    clickStats: ShortUrlStats | null
    logActivities: LogActivity[]
    loading: boolean
    clickStatsLoading: boolean
    error: string | null
    isRefreshing: boolean // Flag to prevent concurrent refresh calls
}

export const useCampaignDetailStore = defineStore('campaignDetail', {
    state: (): CampaignDetailState => ({
        campaign: null,
        clickStats: null,
        logActivities: [],
        loading: false,
        clickStatsLoading: false,
        error: null,
        isRefreshing: false,
    }),

    getters: {
        canEdit: (state): boolean => state.campaign?.can_update ?? false,

        routingDateLabel: (state): string => {
            if (!state.campaign) return ''
            const date = state.campaign.routing_executed_at || state.campaign.routing_at
            if (!date) return ''
            return `Routage ${useDateLabel(date)}`
        },

        hasWellpackShortUrl: (state): boolean =>
            !!state.campaign?.message?.is_wellpack_host,

        // Computed stats getters
        total: (state): number => state.campaign?.recipients_count || 0,

        deliveredCount: (state): number => state.campaign?.status_count?.delivered || 0,

        deliveredRate(): number {
            return this.total ? Math.round((this.deliveredCount / this.total) * 100) : 0
        },

        failedCount: (state): number => {
            const s = (state.campaign?.status_count as Campaign['status_count']) ?? {}
            return (s.undeliverable || 0) + (s.failed || 0) + (s.rejected || 0) + (s.expired || 0)
        },

        failedRate(): number {
            return this.total ? Math.round((this.failedCount / this.total) * 100) : 0
        },

        clickCount: (state): number => state.clickStats?.recipient_as_click || 0,

        clickRate(): string {
            if (this.deliveredCount === 0 || this.clickCount === 0) return '0'
            const percentage = (this.clickCount / this.deliveredCount) * 100
            if (percentage > 0 && percentage < 1) return '<1'
            return Math.round(percentage).toString()
        },
    },

    actions: {
        async fetchCampaign(id: string, params: object = {}, forceFresh: boolean = false) {
            this.loading = true
            this.error = null

            const defaultParams = {
                include: 'message.landingPagePreview,router,recipientsCount,statusesCount,shortUrlStats,variableSchema.variableFields'
            }

            try {
                const { $customFetch } = useNuxtApp()
                const headers: Record<string, string> = {}
                if (forceFresh) {
                    headers['X-Force-Fresh'] = '1'
                }

                const { data } = await $customFetch<ApiResponse<Campaign>>(`/api/campaigns/${id}`, {
                    method: 'GET',
                    params: { ...defaultParams, ...params },
                    headers,
                })

                this.campaign = data

                // Fetch related data in parallel
                await Promise.all([
                    this.fetchClickStats(id),
                    this.fetchLogActivities(id),
                ])
            } catch (e: unknown) {
                this.error = e instanceof Error ? e.message : 'Une erreur est survenue'
                console.error('Error fetching campaign:', e)
            } finally {
                this.loading = false
            }
        },

        async fetchClickStats(id: string) {
            if (!this.hasWellpackShortUrl && this.campaign) {
                // Check again after campaign is loaded
                if (!this.campaign.message?.is_wellpack_host) {
                    this.clickStats = null
                    return
                }
            }

            this.clickStatsLoading = true
            try {
                const { $customFetch } = useNuxtApp()
                const response = await $customFetch<{ data: ShortUrlStats }>(`/api/campaigns/${id}/stats`, {
                    params: { type: 'click' }
                })
                this.clickStats = response.data
            } catch (e) {
                console.error('Failed to fetch click stats:', e)
                this.clickStats = null
            } finally {
                this.clickStatsLoading = false
            }
        },

        async fetchLogActivities(id: string) {
            try {
                const { $customFetch } = useNuxtApp()
                const response = await $customFetch<ApiResponseList<LogActivity>>(`/api/campaigns/${id}/log-activities`, {
                    method: 'GET',
                    params: { include: 'causer' }
                })
                this.logActivities = response.data
            } catch (e) {
                console.error('Failed to fetch log activities:', e)
                this.logActivities = []
            }
        },

        async updateCampaign(updates: CampaignUpdateOptions) {
            if (!this.campaign) return

            const { $customFetch } = useNuxtApp()
            await $customFetch(`/api/campaigns/${this.campaign.id}`, {
                method: 'PUT',
                body: updates
            })

            await this.fetchCampaign(this.campaign.id)
        },

        async refreshCampaign(forceFresh: boolean = false) {
            if (!this.campaign || this.isRefreshing) return
            
            this.isRefreshing = true
            try {
                await this.fetchCampaign(this.campaign.id, {}, forceFresh)
            } finally {
                this.isRefreshing = false
            }
        },

        clear() {
            this.campaign = null
            this.clickStats = null
            this.logActivities = []
            this.loading = false
            this.clickStatsLoading = false
            this.error = null
            this.isRefreshing = false
        },
    },
})
